/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlertColor } from '@mui/material/Alert';
import { usePutAutoAcceptSettings } from 'modules/Settings/hooks/usePutAutoAcceptSettings';
import { useSnackbarStyles } from 'modules/common/hooks/useSnackbarStyles';
import { ToastSnackBar } from 'modules/Settings/ToastSnackBar';
import AutoAcceptSettings from './AutoAcceptSettings';

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles((theme) => ({
  dialog: {
    maxWidth: theme.spacing(57)
  },
  title: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.spacing(2.5),
    lineHeight: 1.6,
    fontFamily: theme.typography.h6.fontFamily
  }
}));

const SettingsDialog = ({
  open,
  onClose
}: SettingsDialogProps): JSX.Element => {
  const classes = useStyles();
  const [severity, setSeverity] = useState<AlertColor>('success');

  const [showAlert, setAlert] = useState(false);

  const { putAutoAcceptSettings } = usePutAutoAcceptSettings();

  const handleSave = useCallback(
    async ({ autoAcceptEnabled }: { autoAcceptEnabled: boolean }) => {
      onClose();
      try {
        await putAutoAcceptSettings({ enabled: autoAcceptEnabled });
      } catch {
        setSeverity('error');
      }
      setAlert(true);
    },
    [onClose, putAutoAcceptSettings]
  );
  const { formatMessage } = useIntl();

  const alertMessage = useMemo(() => {
    return formatMessage({ id: `settingsDialog.${severity}` });
  }, [formatMessage, severity]);

  const handleCloseAlert = useCallback(() => {
    setAlert(false);
  }, []);

  return (
    <>
      <Dialog
        data-testid="settings-dialog"
        open={open}
        classes={{ paper: classes.dialog }}
      >
        <DialogTitle className={classes.title}>
          <FormattedMessage id="settingsDialog.settings" />
        </DialogTitle>
        <AutoAcceptSettings onClose={onClose} onSave={handleSave} />
      </Dialog>
      <ToastSnackBar
        show={showAlert}
        category={severity}
        message={alertMessage}
        onClose={handleCloseAlert}
      />
    </>
  );
};

export default React.memo(SettingsDialog);
